import { useState } from 'react';
import AiTermsGate from '../ai/AiTermsGate';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { useAiAccess, refreshAiAccess } from '../ai/useAiAccess';
import CreditsBar from '../ai/CreditsBar';
import WhereButton from '../ai/WhereButton';

const NOT_MEDICAL = 'EasyLearning AI gives general teaching ideas. It is not medical advice, and it does not replace a doctor, therapist, teacher or other professional who knows the learner. For anything about health, safety or wellbeing, speak to a professional.';
const NOT_MEDICAL_SHORT = 'General teaching ideas only. Not medical advice, and not a replacement for professionals who know the learner.';

type Source = { title: string; url: string };
type Turn = { role: 'user' | 'assistant'; text: string; sent?: string; sources?: Source[]; credits?: number; requestId?: string };
type Suggestion = { title: string; why: string; how: string };

const DATA: { id: string; label: string }[] = [
  { id: 'names', label: 'Program names and categories' },
  { id: 'scores', label: 'Scores from the last 30 days' },
  { id: 'items', label: 'Words and items practised' },
  { id: 'settings', label: 'How each program is set up' },
  { id: 'preferences', label: 'Results of the preference check' },
];

function DataChoices({ name, chosen, onChange }: { name: string; chosen: string[]; onChange: (next: string[]) => void }) {
  const t = useT();
  return (
    <fieldset className="ai-data">
      <legend className="field-label">{t('Also send from EasyLearning')}</legend>
      {DATA.map((d) => (
        <label key={d.id} className="ai-data-option">
          <input type="checkbox" name={name} checked={chosen.includes(d.id)}
            onChange={(e) => onChange(e.target.checked ? [...chosen, d.id] : chosen.filter((x) => x !== d.id))} />
          <span>{t(d.label)}</span>
        </label>
      ))}
      <p className="field-hint">{t("Never sent: the learner's name, age, photos, recordings or anything about you. Names inside what is sent are replaced.")}</p>
    </fieldset>
  );
}

function LearnerChoice({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  const t = useT();
  const { learners } = useLearner();
  if (learners.length < 2) return null;
  return (
    <div className="field">
      <label htmlFor={id}>{t('Learner')}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {learners.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
}

function Ask() {
  const t = useT();
  const { learners, learnerId } = useLearner();
  const [include, setInclude] = useState<string[]>([]);
  const [who, setWho] = useState<string>('');
  const chosenLearner = who || learnerId || learners[0]?.id || '';
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    const q = question.trim();
    if (!q) return;
    setBusy(true);
    setError(null);
    try {
      const history = turns.slice(-10).map((turn) => ({ role: turn.role, text: turn.role === 'user' ? (turn.sent ?? turn.text) : turn.text }));
      const r = await api<{ answer: string; sent: string; sources: Source[]; credits: number; requestId: string }>('/ai/advice', { method: 'POST', token: true, body: { question: q, history, include, learnerId: include.length ? chosenLearner || null : null } });
      setTurns((prev) => [...prev, { role: 'user', text: q, sent: r.sent }, { role: 'assistant', text: r.answer, sources: r.sources, credits: r.credits, requestId: r.requestId }]);
      refreshAiAccess();
      setQuestion('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('No answer came back.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card ai-card">
      <h2 style={{ marginTop: 0 }}>{t('Ask about teaching')}</h2>
      <p className="field-hint" style={{ marginTop: 0 }}>{t('Ask anything about teaching or supporting a learner. The advice is for you to weigh up; you decide what to use.')}</p>
      {turns.length > 0 && (
        <div className="ai-turns">
          {turns.map((turn, i) => (
            <div key={i} className={`ai-turn is-${turn.role}`}>
              <p className="ai-who">{turn.role === 'user' ? t('You') : t('EasyLearning AI')}</p>
              <div className="ai-text">{turn.text}</div>
              {turn.sources && turn.sources.length > 0 && (
                <div className="ai-sources">
                  <p className="field-label">{t('Sources it used')}</p>
                  <ol>{turn.sources.map((src) => <li key={src.url}><a href={src.url} target="_blank" rel="noopener noreferrer">{src.title}</a></li>)}</ol>
                </div>
              )}
              {turn.role === 'assistant' && <p className="field-hint ai-turn-note">{t(NOT_MEDICAL_SHORT)}{turn.credits ? ` ${turn.credits === 1 ? t('Used {n} credit.', { n: turn.credits }) : t('Used {n} credits.', { n: turn.credits })}` : ''}</p>}
              {turn.role === 'assistant' && <WhereButton ids={[turn.requestId]} />}
              {turn.sent && turn.sent !== turn.text && (
                <details className="ai-sent"><summary>{t('What was sent')}</summary><p>{turn.sent}</p></details>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="field">
        <label htmlFor="ai-q">{turns.length ? t('Ask a follow-up') : t('Your question')}</label>
        <textarea id="ai-q" rows={3} maxLength={2000} value={question} placeholder={t('e.g. How do I fade prompts when matching pictures?')}
          onChange={(e) => setQuestion(e.target.value)} />
        <p className="field-hint">{t('Names of your learners and you, email addresses, phone numbers, postcodes and dates are removed before it is sent.')}</p>
      </div>
      <DataChoices name="ask-data" chosen={include} onChange={setInclude} />
      {include.length > 0 && <LearnerChoice id="ai-ask-learner" value={chosenLearner} onChange={setWho} />}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || !question.trim()} onClick={() => void send()}>{busy ? t('Thinking') : t('Ask')}</button>
        {turns.length > 0 && <button type="button" className="btn secondary" disabled={busy} onClick={() => setTurns([])}>{t('Start a new conversation')}</button>}
      </div>
    </div>
  );
}

function History() {
  const t = useT();
  const [rows, setRows] = useState<{ id: string; feature: string; at: string; summary: string }[] | null>(null);
  return (
    <div className="card ai-card">
      <h2 style={{ marginTop: 0 }}>{t('Where your data went')}</h2>
      <p className="field-hint" style={{ marginTop: 0 }}>{t('Every request to EasyLearning AI, with exactly what was sent and where it went.')}</p>
      {rows === null
        ? <button type="button" className="btn secondary" onClick={() => void api<typeof rows>('/ai/requests', { token: true }).then((r) => setRows(r ?? [])).catch(() => setRows([]))}>{t('Show my recent requests')}</button>
        : rows.length === 0 ? <p className="muted">{t('No requests yet.')}</p>
          : <ul className="flow-history">{rows.map((r) => (
            <li key={r.id}><span><strong>{r.feature}</strong> · {new Date(r.at).toLocaleString()}<br /><span className="muted">{r.summary}</span></span><WhereButton ids={[r.id]} /></li>
          ))}</ul>}
    </div>
  );
}

function NextSteps() {
  const t = useT();
  const { learners, learnerId } = useLearner();
  const [who, setWho] = useState<string>(learnerId ?? '');
  const [include, setInclude] = useState<string[]>(['names', 'scores']);
  const [result, setResult] = useState<{ suggestions: Suggestion[]; sent: string; credits: number; requestId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = who || learnerId || learners[0]?.id || '';

  async function suggest() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const q = include.map((i) => `&include=${encodeURIComponent(i)}`).join('');
      setResult(await api(`/ai/next-steps?learnerId=${encodeURIComponent(chosen)}${q}`, { method: 'POST', token: true }));
      refreshAiAccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('No suggestions came back.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card ai-card">
      <h2 style={{ marginTop: 0 }}>{t('What to teach next')}</h2>
      <p className="field-hint" style={{ marginTop: 0 }}>{t('Suggestions from what the learner practises in EasyLearning. You choose what is sent.')}</p>
      <LearnerChoice id="ai-learner" value={chosen} onChange={(v) => { setWho(v); setResult(null); }} />
      <DataChoices name="next-data" chosen={include} onChange={setInclude} />
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || !chosen || include.length === 0} onClick={() => void suggest()}>{busy ? t('Looking at the scores') : t('Suggest what to teach next')}</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {result && (
        <>
          <ol className="ai-suggestions">
            {result.suggestions.map((s, i) => (
              <li key={i}>
                <p className="name">{s.title}</p>
                <p>{s.why}</p>
                <p className="muted">{s.how}</p>
              </li>
            ))}
          </ol>
          <p className="field-hint">{t(NOT_MEDICAL_SHORT)}{result.credits ? ` ${result.credits === 1 ? t('Used {n} credit.', { n: result.credits }) : t('Used {n} credits.', { n: result.credits })}` : ''}</p>
          <WhereButton ids={[result.requestId]} />
          <details className="ai-sent"><summary>{t('What was sent')}</summary><p>{result.sent}</p></details>
        </>
      )}
    </div>
  );
}

export default function AiPage() {
  const t = useT();
  const { access } = useAiAccess();
  if (!access?.visible) {
    return (
      <div className="card">
        <h1>{t('EasyLearning AI')}</h1>
        <p className="muted">{t('This is not available on this account yet.')}</p>
      </div>
    );
  }
  if (!access.termsAccepted) return <AiTermsGate access={access} />;
  return (
    <div className="ai-page">
      <div className="card">
        <h1 style={{ margin: 0 }}>{t('EasyLearning AI')}</h1>
        <p>{t('Advice for you, the adult. It never sees anything that identifies a learner.')}</p>
        <p className="ai-notice">{t(NOT_MEDICAL)}</p>
        <CreditsBar access={access} />
      </div>
      <Ask />
      <NextSteps />
      <History />
    </div>
  );
}
